import { useContext } from 'react';
import React, { useState, useEffect } from 'react';
import { Search, Plus, Pencil, Trash2, X, Upload, Download, Settings, Users } from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import axios from '../../axios-config';
import UserClaimsModal from './UserClaimsModal';
import RoleManagementModal from './RoleManagementModal';
import { UserManagementService } from '../../services/userManagementService';
import { ClaimType, UserClaim } from '../../types/user-management';

export interface Student {
  id: string;
  student_id: string;
  name: string;
  email: string;
  grade: number;
  role: 'STUDENT' | 'CLERK';
  birthday: string;
  created_at: string;
  updated_at: string;
}

export interface Staff {
  id: string;
  name: string;
  email: string;
  position: string;
  location: string;
  role: 'Manager' | 'User' | 'Advisor' | 'Operator' | 'Involvement Operator' | 'Behavior/Intervention Operator' | 'Redemptions Operator' | 'Viewer';
  birth_month: number;
  birth_day: number;
  created_at: string;
  updated_at: string;
}

export interface Teacher {
  id: string;
  name: string;
  email: string;
  role: string; // Typically 'Teacher'
  created_at: string;
  updated_at: string;
}

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (data: any[]) => void;
  type: 'students' | 'teachers' | 'staff';
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ isOpen, onClose, onUpload, type }) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadUploadStudentsResult, setUploadResult] = useState<{ added: number; mergeCount: Number, skipped: number; errors: string[] } | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const _uctx: any = useContext(UserContext);
  const { refreshProfile } = _uctx || {};

  // --- Helpers to summarize line-based errors like
  // "Line 380: Missing AltId/studentNumber." ---
  const compressLineNumbers = (nums: number[]) => {
    if (!nums.length) return '';
    nums.sort((a, b) => a - b);
    const ranges: string[] = [];
    let start = nums[0], prev = nums[0];
    for (let i = 1; i < nums.length; i++) {
      const n = nums[i];
      if (n === prev + 1) {
        prev = n;
        continue;
      }
      ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
      start = prev = n;
    }
    ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
    return ranges.join(', ');
  };

  const errorSummary = React.useMemo(() => {
    const result = uploadUploadStudentsResult;
    if (!result?.errors?.length) return [] as { message: string; lines: number[]; count: number }[];
    const map = new Map<string, { message: string; lines: number[]; count: number }>();
    for (const raw of result.errors) {
      const m = /Line\s*(\d+)\s*:\s*(.*)/i.exec(String(raw));
      const line = m ? Number(m[1]) : undefined;
      let msg = (m ? m[2] : String(raw)).trim();
      if (msg.endsWith('.')) msg = msg.slice(0, -1);
      const key = msg.toLowerCase();
      if (!map.has(key)) map.set(key, { message: msg, lines: [], count: 0 });
      const entry = map.get(key)!;
      if (typeof line === 'number' && !Number.isNaN(line)) entry.lines.push(line);
      entry.count += 1;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [uploadUploadStudentsResult]);

  const handleUploadStudentsDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const processUploadStudentsFile = async (file: File) => {
    setError(null);
    const form = new FormData();
    form.append('file', file);
    // choose endpoint based on type
    const url = type === 'students'
      ? 'api/Account/UploadStudents'
      : type === 'teachers'
        ? 'api/Account/UploadTeachers'
        : 'api/Account/UploadStaff';
    try {
      const { data } = await axios.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(data);
      onUpload([]); // trigger parent refresh if needed
      // Refresh the global user profile via context if available
      try {
        if (typeof refreshProfile === 'function') {
          await refreshProfile();
        } else {
          const maybeRefresh = (window as any).__refreshProfile;
          if (typeof maybeRefresh === 'function') await maybeRefresh();
        }
      } catch (refreshErr) {
        console.warn('refreshProfile failed after bulk upload', refreshErr);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const handleUploadStudentsDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await processUploadStudentsFile(e.dataTransfer.files[0]);
    }
  };

  const handleUploadStudentsChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setError(null);

    if (e.target.files && e.target.files[0]) {
      await processUploadStudentsFile(e.target.files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    let csv: string;
    const headers = `Email,AltId,Name,Avatar,BirthDate,Role,Grade,ClassName,initialGeneralPoints,initialSavingsPoints\n`;
    if (type === 'students') {
      // full sample data for students
      csv = headers + `alex.student@example.com,EXP345,Alex Student,https://example.com/avatars/alex.png,2011-09-14,Student,7,Acorn Jr. Test Class,100,50
      beth.anywhere@example.com,XYZ67890,Beth Anywhere,https://example.com/avatars/beth.png,2012-02-03,Student,6,Ms. Johnson's Class,75,35`;
    } else if (type === 'teachers') {
      csv = headers + `tina.teacher@example.com,TCH123,Tina Teacher,,1984-07-22,Teacher,0,Homeroom 3A,0,0`;
    } else {
      csv = headers + `charlie.teacher@example.com,TCH001,Charlie Teacher,,1984-07-22,Teacher,1,Mr. Smith's Class,0,0`;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCloseModal = () => {
    setUploadResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      {/* Make the modal body scrollable to avoid overflow when there are many errors */}
      <div className="bg-white rounded-lg max-w-xl w-full p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Bulk Upload {type === 'students' ? 'Students' : 'Staff'}</h2>
          <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!uploadUploadStudentsResult ? (
          <>
            <div className="mb-6">
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
              >
                <Download className="w-4 h-4" /> Download CSV Template
              </button>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}
              onDragEnter={handleUploadStudentsDrag}
              onDragLeave={handleUploadStudentsDrag}
              onDragOver={handleUploadStudentsDrag}
              onDrop={handleUploadStudentsDrop}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Drag and drop your CSV file here, or{' '}
                <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                  browse
                  <input
                    type="file"
                    className="hidden"
                    accept=".csv"
                    onChange={handleUploadStudentsChange}
                  />
                </label>
              </p>
              <p className="text-sm text-gray-500">Supports CSV files only</p>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
          </>
        ) : (
          <div className="mt-4">
            <p>Imported: {uploadUploadStudentsResult.added}</p>
            <p>Merged: {Number(uploadUploadStudentsResult.mergeCount as unknown as number)}</p>
            <p>Skipped: {uploadUploadStudentsResult.skipped}</p>
            {uploadUploadStudentsResult.errors.length > 0 && (
              <div className="mt-3">
                <h3 className="font-medium text-gray-800 mb-1">Issues summary</h3>
                <ul className="ml-5 list-disc text-sm text-red-700">
                  {errorSummary.map((g, i) => (
                    <li key={i} className="mb-1">
                      <span className="font-medium">{g.message}</span>{' '}
                      {g.lines.length > 0 && (
                        <>
                          – lines {compressLineNumbers(g.lines)}
                        </>
                      )}
                      {' '}({g.count})
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => setShowErrorDetails(v => !v)}
                  className="mt-3 text-xs text-blue-600 hover:text-blue-700"
                >
                  {showErrorDetails ? 'Hide details' : 'Show details'}
                </button>

                {showErrorDetails && (
                  <div className="mt-2 max-h-64 overflow-auto border border-red-200 rounded p-2 bg-red-50">
                    <ul className="list-disc ml-5 text-sm text-red-700">
                      {uploadUploadStudentsResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={handleCloseModal} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  data?: Student | Teacher | Staff;
  type: 'students' | 'teachers' | 'staff';
  availableRoles: any[];
}

const EditModal: React.FC<EditModalProps> = ({ isOpen, onClose, onSave, data, type, availableRoles }) => {
  const [formData, setFormData] = useState<any>({});

  // Update form data when modal data changes
  React.useEffect(() => {
    setFormData(data || {});
  }, [data]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {data ? 'Edit' : 'Add'} {type === 'students' ? 'Student' : type === 'teachers' ? 'Teacher' : 'Staff'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {type === 'students' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Student ID</label>
                <input
                  type="text"
                  name="student_id"
                  value={(formData as Student).student_id || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Grade</label>
                <input
                  type="number"
                  name="grade"
                  value={(formData as Student).grade || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                  min="1"
                  max="12"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {availableRoles && availableRoles.length > 0 ? (
                    availableRoles.map((r: any) => {
                      const roleName = r.name;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, role: roleName })}
                          className={`px-3 py-1 rounded border text-xs transition-colors ${(formData as any).role === roleName ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'}`}
                        >
                          {roleName}
                        </button>
                      );
                    })
                  ) : (
                    <span className="text-xs text-gray-500">No roles loaded</span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Birthday</label>
                <input
                  type="date"
                  name="birthday"
                  value={(formData as Student).birthday
                    ? new Date((formData as Student).birthday).toISOString().split('T')[0]
                    : ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
            </>
          ) : type === 'teachers' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {availableRoles && availableRoles.length > 0 ? (
                    availableRoles.map((r: any) => {
                      const roleName = r.name;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, role: roleName })}
                          className={`px-3 py-1 rounded border text-xs transition-colors ${(formData as any).role === roleName ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'}`}
                        >
                          {roleName}
                        </button>
                      );
                    })
                  ) : (
                    <span className="text-xs text-gray-500">No roles loaded</span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Position</label>
                <input
                  type="text"
                  name="position"
                  value={(formData as Staff).position || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  name="location"
                  value={(formData as Staff).location || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {availableRoles && availableRoles.length > 0 ? (
                    availableRoles.map((r: any) => {
                      const roleName = r.name;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, role: roleName })}
                          className={`px-3 py-1 rounded border text-xs transition-colors ${(formData as any).role === roleName ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'}`}
                        >
                          {roleName}
                        </button>
                      );
                    })
                  ) : (
                    <span className="text-xs text-gray-500">No roles loaded</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Birth Month</label>
                  <input
                    type="number"
                    name="birth_month"
                    value={(formData as Staff).birth_month || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    required
                    min="1"
                    max="12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Birth Day</label>
                  <input
                    type="number"
                    name="birth_day"
                    value={(formData as Staff).birth_day || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    required
                    min="1"
                    max="31"
                  />
                </div>
              </div>
            </>
          )}
          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserManagement: React.FC = () => {
  const { hasClaim, userProfile, fetchUserProfile } = useContext(UserContext);

  // Unified permission flag (supports old/new claim names + elevated roles)
  const canManageUsers = (
    hasClaim?.('CanManageUsers') ||
    hasClaim?.('ManageUsers') ||
    userProfile?.mainRole == 'AcornJrAdmin' ||
    userProfile?.mainRole == 'Admin'
  );

  // Initialize students from userProfile.students
  const [students, setStudents] = useState<Student[]>(() => {
    return (userProfile?.students || []).map((s: any) => ({
      id: s.userId,
      student_id: s.id,
      name: s.name,
      email: s.email || '',
      grade: s.grade || 0,
      role: s.role,
      birthday: s.birthDate,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  });

  const [staff, setStaff] = useState<Staff[]>(() => {
    return (((userProfile as any)?.staff) || []).map((s: any) => ({
      id: s.id || s.userId || crypto.randomUUID(),
      name: s.name || '',
      email: s.email || '',
      position: s.position || '',
      location: s.location || '',
      role: (s.role as Staff['role']) || 'User',
      birth_month: typeof s.birth_month === 'number' ? s.birth_month : (s.birthMonth || s.birth_month || 1),
      birth_day: typeof s.birth_day === 'number' ? s.birth_day : (s.birthDay || s.birth_day || 1),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  });

  const [activeTab, setActiveTab] = useState<'students' | 'teachers' | 'staff'>('students');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllMode, setShowAllMode] = useState(false); // When false (default), user must type at least 1 character to see results
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [editingData, setEditingData] = useState<Student | Teacher | Staff | undefined>();
  const [teachers, setTeachers] = useState<Teacher[]>(() => {
    return (((userProfile as any)?.teachers) || []).map((t: any) => ({
      id: t.id || t.userId || crypto.randomUUID(),
      name: t.name || '',
      email: t.email || '',
      role: t.role || 'Teacher',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  });

  // New state for claims and roles management
  const [isClaimsModalOpen, setIsClaimsModalOpen] = useState(false);
  const [isRoleManagementOpen, setIsRoleManagementOpen] = useState(false);
  const [selectedUserForClaims, setSelectedUserForClaims] = useState<{
    id: string;
    name: string;
    claims: UserClaim[];
  } | null>(null);
  const [availableClaims, setAvailableClaims] = useState<ClaimType[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);

  // Keep local students/teachers/staff in sync when the global userProfile changes
  useEffect(() => {
    if (!userProfile) return;

    // Sync students
    const profStudents = (userProfile?.students || []) as any[];
    setStudents(profStudents.map((s: any) => ({
      id: s.userId,
      student_id: s.id,
      name: s.name,
      email: s.email || '',
      grade: s.grade || 0,
      role: s.role,
      birthday: s.birthDate,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })));

    // Sync teachers
    const profTeachers = ((userProfile as any)?.teachers || []) as any[];
    setTeachers(profTeachers.map((t: any) => ({
      id: t.id || t.userId || crypto.randomUUID(),
      name: t.name || '',
      email: t.email || '',
      role: t.role || 'Teacher',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })));

    // Sync staff
    const profStaff = ((userProfile as any)?.staff || []) as any[];
    setStaff(profStaff.map((s: any) => ({
      id: s.id || s.userId || crypto.randomUUID(),
      name: s.name || '',
      email: s.email || '',
      position: s.position || '',
      location: s.location || '',
      role: (s.role as Staff['role']) || 'User',
      birth_month: typeof s.birth_month === 'number' ? s.birth_month : (s.birthMonth || s.birth_month || 1),
      birth_day: typeof s.birth_day === 'number' ? s.birth_day : (s.birthDay || s.birth_day || 1),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })));
  }, [userProfile]);

  // Load available roles always (not permission-gated) so role selector works
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const rolesData = await UserManagementService.getAvailableRoles();
        const normalizedRoles = (rolesData || []).map((r: any) => ({
          id: r.Id || r.id || r.ID || r.Name || crypto.randomUUID(),
          name: r.Name || r.name || r.NormalizedName || r,
          abbreviation: r.Abbreviation || r.abbreviation || null
        }));
        setAvailableRoles(normalizedRoles);
        // Debug: log roles fetched
        console.debug('[UserManagement] Loaded roles:', normalizedRoles);
      } catch (error) {
        console.error('Failed to load roles:', error);
      }
    };
    loadRoles();
  }, []);

  // Load available claims only if user can manage users
  useEffect(() => {
    const loadClaims = async () => {
      if (!canManageUsers) return;
      try {
        const claimsData = await UserManagementService.getAvailableClaimTypes();
        setAvailableClaims(claimsData);
      } catch (error) {
        console.error('Failed to load claims:', error);
      }
    };
    loadClaims();
  }, [canManageUsers]);

  const handleAdd = () => {
    setEditingData(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (data: Student | Teacher | Staff) => {
    setEditingData(data);
    setIsModalOpen(true);
  };

  const handleManageClaims = async (user: Student | Teacher | Staff) => {
    // Fetch live claims for the selected user (not the current logged-in user's claims)
    try {
      const liveClaims = await UserManagementService.getUserClaims(user.id);
      setSelectedUserForClaims({
        id: user.id,
        name: user.name,
        claims: liveClaims
      });
    } catch (e) {
      console.error('Failed to load user claims', e);
      // Fallback to empty list on failure
      setSelectedUserForClaims({ id: user.id, name: user.name, claims: [] });
    }
    setIsClaimsModalOpen(true);
  };

  const handleRoleManagement = () => {
    setIsRoleManagementOpen(true);
  };

  const refreshData = async () => {
    // Refresh user profile to get updated claims/roles
    try {
      await fetchUserProfile();

      // Reload available claims
      const [claimsData, rolesData] = await Promise.all([
        UserManagementService.getAvailableClaimTypes(),
        UserManagementService.getAvailableRoles()
      ]);
      setAvailableClaims(claimsData);
      const normalizedRoles = (rolesData || []).map((r: any) => ({
        id: r.Id || r.id || r.ID || r.Name || crypto.randomUUID(),
        name: r.Name || r.name || r.NormalizedName || r,
        abbreviation: r.Abbreviation || r.abbreviation || null
      }));
      setAvailableRoles(normalizedRoles);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`api/Account/${id}`);
      if (activeTab === 'students') {
        setStudents(students.filter(student => student.id !== id));
      } else if (activeTab === 'teachers') {
        setTeachers(teachers.filter(teacher => teacher.id !== id));
      } else {
        setStaff(staff.filter(staffMember => staffMember.id !== id));
      }
    } catch (err) {
      console.error('Error deleting user:', err);
    }
  };

  const handleSave = async (data: any) => {
    if (activeTab === 'students') {
      if (editingData) {
        // update local state
        setStudents(students.map(student =>
          student.id === (editingData as Student).id ? { ...data, id: student.id } as Student : student
        ));
        // send PUT to backend
        try {
          await UserManagementService.updateUserRole((editingData as Student).id, {
            id: (editingData as Student).id,
            name: data.name,
            altId: data.student_id,
            email: data.email,
            birthDate: data.birthday,
            role: data.role,
            grade: data.grade
          });
        } catch (err) {
          console.error('Error updating student:', err);
        }
      } else {
        // Create new student on server
        try {
          await axios.post(
            'api/Account/create-account',
            {
              Name: data.name,
              AltId: data.student_id,
              Email: data.email,
              BirthDate: data.birthday,
              Role: data.role,
              Grade: data.grade,
              OrganizationId: userProfile?.orgId
            },
            { headers: { 'Content-Type': 'application/json', 'accept': '*/*' } }
          );
        } catch (err) {
          console.error('Error creating student:', err);
        }
        // then update local state
        setStudents([...students, { ...data, id: crypto.randomUUID() }]);
      }
    } else if (activeTab === 'teachers') {
      if (editingData) {
        setTeachers(teachers.map(teacher =>
          teacher.id === (editingData as Teacher).id ? { ...teacher, ...data, id: teacher.id } as Teacher : teacher
        ));
        try {
          await UserManagementService.updateUserRole((editingData as Teacher).id, {
            id: (editingData as Teacher).id,
            name: data.name,
            email: data.email,
            role: data.role || 'Teacher'
          });
        } catch (err) {
          console.error('Error updating teacher:', err);
        }
      } else {
        try {
          await axios.post(
            'api/Account/create-account',
            {
              Name: data.name,
              Email: data.email,
              Role: data.role || 'Teacher',
              OrganizationId: userProfile?.orgId
            },
            { headers: { 'Content-Type': 'application/json', 'accept': '*/*' } }
          );
        } catch (err) {
          console.error('Error creating teacher:', err);
        }
        setTeachers([...teachers, { ...(data as any), id: crypto.randomUUID(), created_at: new Date().toISOString(), updated_at: new Date().toISOString(), role: data.role || 'Teacher' } as Teacher]);
      }
    } else {
      if (editingData) {
        // update local state
        setStaff(staff.map(staffMember =>
          staffMember.id === (editingData as Staff).id ? { ...staffMember, ...data, id: staffMember.id } as Staff : staffMember
        ));
        // send PUT to backend for staff update
        try {
          await UserManagementService.updateUserRole((editingData as Staff).id, {
            id: (editingData as Staff).id,
            name: data.name,
            email: data.email,
            role: data.role,
            // Note: position, location, birthMonth, birthDay are not part of the role update API
            // They would need separate endpoints if they need to be updated
          });
        } catch (err) {
          console.error('Error updating staff:', err);
        }
      } else {
        // create new staff on server
        try {
          await axios.post(
            'api/Account/create-account',
            {
              Name: data.name,
              Email: data.email,
              Role: data.role,
              Position: data.position,
              Location: data.location,
              BirthMonth: Number(data.birth_month) || Number(data.birthMonth) || undefined,
              BirthDay: Number(data.birth_day) || Number(data.birthDay) || undefined,
              OrganizationId: userProfile?.orgId
            },
            { headers: { 'Content-Type': 'application/json', 'accept': '*/*' } }
          );
        } catch (err) {
          console.error('Error creating staff:', err);
        }
        setStaff([...staff, { ...(data as any), id: crypto.randomUUID(), created_at: new Date().toISOString(), updated_at: new Date().toISOString() } as Staff]);
      }
    }
  };

  const handleBulkUpload = (data: any[]) => {
    if (activeTab === 'students') {
      const newStudents = data.map(item => ({
        ...item,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));
      setStudents([...students, ...newStudents]);
    } else if (activeTab === 'teachers') {
      const newTeachers = data.map(item => ({
        ...item,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        role: item.role || 'Teacher'
      }));
      setTeachers([...teachers, ...newTeachers]);
    } else {
      const newStaff = data.map(item => ({
        ...item,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));
      setStaff([...staff, ...newStaff]);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Manage Users</h1>
          <p className="mt-2 text-gray-600">Manage students and staff information</p>
        </div>
        {/* Bulk Upload Modal */}

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm">
          {/* Tabs */}

          {hasClaim('CanManageStaff') && (
            <div className="border-b border-gray-200">
              <div className="flex gap-6 px-6">
                <button
                  className={`py-4 px-2 -mb-px text-sm font-medium ${activeTab === 'students'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                  onClick={() => setActiveTab('students')}
                >
                  Students
                </button>
                <button
                  className={`py-4 px-2 -mb-px text-sm font-medium ${activeTab === 'teachers'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                  onClick={() => setActiveTab('teachers')}
                >
                  Teachers
                </button>
                <button
                  className={`py-4 px-2 -mb-px text-sm font-medium ${activeTab === 'staff'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                  onClick={() => setActiveTab('staff')}
                >
                  Staff
                </button>
              </div>
            </div>
          )}

          {/* Bulk Upload Modal */}

          {/* Actions */}
          <div className="p-6 flex flex-col sm:flex-row gap-4 border-b border-gray-200">
            <div className="flex-1">
              {/* input + icon */}
              <div className="relative">
                <Search
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5"
                />
                <input
                  type="text"
                  placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* checkbox/help text */}
              <div className="mt-3 ml-10 flex items-center gap-3 text-sm">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                    checked={showAllMode}
                    onChange={(e) => setShowAllMode(e.target.checked)}
                  />
                  <span className="text-gray-700 font-medium">Show All</span>
                </label>
                {!showAllMode && (
                  <span className="text-gray-500 text-xs">Enter at least 1 character to view results OR check Show All</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {canManageUsers && (
                <button
                  onClick={handleRoleManagement}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Settings className="w-5 h-5" />
                  <span>Manage Roles</span>
                </button>
              )}
              <button
                onClick={() => setIsBulkUploadOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Upload className="w-5 h-5" />
                <span>Bulk Upload</span>
              </button>
              <button
                onClick={handleAdd}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Add {activeTab === 'students' ? 'Student' : activeTab === 'teachers' ? 'Teacher' : 'Staff'}</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {activeTab === 'students' ? (
                    <>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student ID
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Grade
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Birthday
                      </th>
                    </>
                  ) : activeTab === 'teachers' ? (
                    <>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Position
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Birthday
                      </th>
                    </>
                  )}
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {searchTerm.trim().length === 0 && !showAllMode ? (
                  <tr>
                    <td
                      colSpan={activeTab === 'students' ? 7 : activeTab === 'teachers' ? 4 : 6}
                      className="py-10 px-6 text-center text-gray-500"
                    >
                      Start typing to search users (or enable "Show all")
                    </td>
                  </tr>
                ) : activeTab === 'students' ? (
                  students
                    .filter((student) => {
                      const search = (searchTerm || '').toLowerCase();
                      // If showAllMode is enabled and no search term, show all students
                      if (search.length === 0 && showAllMode) return true;
                      const name = (student?.name || '').toLowerCase();
                      const sid = (student?.student_id || '').toLowerCase();
                      const email = (student?.email || '').toLowerCase();
                      return (
                        name.includes(search) || sid.includes(search) || email.includes(search)
                      );
                    })
                    .map((student) => (
                      <tr key={student.id}>
                        <td className="py-4 px-6">{student.student_id}</td>
                        <td className="py-4 px-6">{student.name}</td>
                        <td className="py-4 px-6">{student.email}</td>
                        <td className="py-4 px-6">{student.grade}</td>
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${student.role === 'CLERK'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-blue-100 text-blue-700'
                            }`}>
                            {student.role}
                          </span>
                        </td>
                        <td className="py-4 px-6">{formatDate(student.birthday)}</td>
                        <td className="py-4 px-6">
                          <div className="flex gap-3">
                            <button
                              className="text-gray-400 hover:text-blue-600"
                              onClick={() => handleEdit(student)}
                              title="Edit user"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            {canManageUsers && (
                              <button
                                className="text-gray-400 hover:text-purple-600"
                                onClick={() => handleManageClaims(student)}
                                title="Manage claims"
                              >
                                <Users className="w-5 h-5" />
                              </button>
                            )}
                            <button
                              className="text-gray-400 hover:text-red-600"
                              onClick={() => handleDelete(student.id)}
                              title="Delete user"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : activeTab === 'teachers' ? (
                  teachers
                    .filter((teacher) => {
                      const search = (searchTerm || '').toLowerCase();
                      // If showAllMode is enabled and no search term, show all teachers
                      if (search.length === 0 && showAllMode) return true;
                      const name = (teacher?.name || '').toLowerCase();
                      const email = (teacher?.email || '').toLowerCase();
                      return (
                        name.includes(search) || email.includes(search)
                      );
                    })
                    .map((teacher) => (
                      <tr key={teacher.id}>
                        <td className="py-4 px-6">{teacher.name}</td>
                        <td className="py-4 px-6">{teacher.email}</td>
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700`}>
                            {teacher.role || 'Teacher'}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex gap-3">
                            <button
                              className="text-gray-400 hover:text-blue-600"
                              onClick={() => handleEdit(teacher)}
                              title="Edit user"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            {canManageUsers && (
                              <button
                                className="text-gray-400 hover:text-purple-600"
                                onClick={() => handleManageClaims(teacher)}
                                title="Manage claims"
                              >
                                <Users className="w-5 h-5" />
                              </button>
                            )}
                            <button
                              className="text-gray-400 hover:text-red-600"
                              onClick={() => handleDelete(teacher.id)}
                              title="Delete user"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  staff
                    .filter((staffMember) => {
                      const search = (searchTerm || '').toLowerCase();
                      // If showAllMode is enabled and no search term, show all staff
                      if (search.length === 0 && showAllMode) return true;
                      const name = (staffMember?.name || '').toLowerCase();
                      const email = (staffMember?.email || '').toLowerCase();
                      const position = (staffMember?.position || '').toLowerCase();
                      return (
                        name.includes(search) || email.includes(search) || position.includes(search)
                      );
                    })
                    .map((staffMember) => (
                      <tr key={staffMember.id}>
                        <td className="py-4 px-6">{staffMember.name}</td>
                        <td className="py-4 px-6">{staffMember.position}</td>
                        <td className="py-4 px-6">{staffMember.location}</td>
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${staffMember.role === 'Manager'
                            ? 'bg-purple-100 text-purple-700'
                            : staffMember.role === 'Operator'
                              ? 'bg-blue-100 text-blue-700'
                              : staffMember.role === 'Advisor'
                                ? 'bg-green-100 text-green-700'
                                : staffMember.role === 'Involvement Operator'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : staffMember.role === 'Behavior/Intervention Operator'
                                    ? 'bg-red-100 text-red-700'
                                    : staffMember.role === 'Redemptions Operator'
                                      ? 'bg-indigo-100 text-indigo-700'
                                      : staffMember.role === 'Viewer'
                                        ? 'bg-pink-100 text-pink-700'
                                        : 'bg-gray-100 text-gray-700'
                            }`}>
                            {staffMember.role}
                          </span>
                        </td>
                        <td className="py-4 px-6">{`${staffMember.birth_month}/${staffMember.birth_day}`}</td>
                        <td className="py-4 px-6">
                          <div className="flex gap-3">
                            <button
                              className="text-gray-400 hover:text-blue-600"
                              onClick={() => handleEdit(staffMember)}
                              title="Edit user"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            {canManageUsers && (
                              <button
                                className="text-gray-400 hover:text-purple-600"
                                onClick={() => handleManageClaims(staffMember)}
                                title="Manage claims"
                              >
                                <Users className="w-5 h-5" />
                              </button>
                            )}
                            <button
                              className="text-gray-400 hover:text-red-600"
                              onClick={() => handleDelete(staffMember.id)}
                              title="Delete user"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <EditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        data={editingData}
        type={activeTab}
        availableRoles={availableRoles}
      />
      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onUpload={handleBulkUpload}
        type={activeTab}
      />
      {selectedUserForClaims && (
        <UserClaimsModal
          isOpen={isClaimsModalOpen}
          onClose={() => {
            setIsClaimsModalOpen(false);
            setSelectedUserForClaims(null);
          }}
          userId={selectedUserForClaims.id}
          userName={selectedUserForClaims.name}
          userClaims={selectedUserForClaims.claims}
          availableClaims={availableClaims}
          onRefresh={refreshData}
        />
      )}
      <RoleManagementModal
        isOpen={isRoleManagementOpen}
        onClose={() => setIsRoleManagementOpen(false)}
        roles={availableRoles}
        availableClaims={availableClaims}
        onRefresh={refreshData}
      />
    </div>
  );
};

export default UserManagement;